/**
 * PLAT-11223 — editing-side TTFE events, mounted once at the Creator Studio
 * root (`VideoCreator`):
 *
 *   - `ttfe.first_edit_accepted` (clock stop) on the first executed editor
 *     command. COMMAND_EXECUTED is the single chokepoint for user edits —
 *     transcript and timeline mutations are all commands, while load-time
 *     reconciles (which also call `markDirty`) never notify it.
 *   - `ttc.canvas_ready` (second metric) when the renderer handshake
 *     completes (`isVideoCreatorReady`).
 *
 * Both are once-per-upload-session; the dedupe (and the no-session no-op for
 * old stories) lives in TtfeTracker.
 */
import { EditorChangeType } from '@src/editor/types';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { when } from 'mobx';
import { useEffect } from 'react';

import * as TtfeTracker from '../TtfeTracker';

import type { EditorObserver } from '@src/editor/types';

export function useTtfeEditorTracking(): void {
  const rootStore = useStore();
  const videoCreator = useVideoCreatorStore();

  useEffect(() => {
    const editor = rootStore.editorStore.editor;
    const observer: EditorObserver = {
      onEditorChange: (_editor, _changeType, payload) => {
        const storyId = videoCreator.storyId;
        if (!storyId) return;
        const commandName: string = payload?.command?.getName?.() ?? '';
        TtfeTracker.firstEditAccepted(
          storyId,
          commandName.startsWith('Transcript') ? 'transcript' : 'timeline',
        );
      },
    };
    editor.registerObserver(observer, [EditorChangeType.COMMAND_EXECUTED]);
    return () => {
      editor.unregisterObserver(observer, [EditorChangeType.COMMAND_EXECUTED]);
    };
  }, [rootStore, videoCreator]);

  useEffect(() => {
    return when(
      () => videoCreator.isVideoCreatorReady && !!videoCreator.storyId,
      () => {
        const { storyId } = videoCreator;
        if (storyId) TtfeTracker.canvasReady(storyId, '270p');
      },
    );
  }, [videoCreator]);
}
